import { useTranslation } from "react-i18next";

import type { ExamHistoryEntry } from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamHistoryPanel
 *
 * Renders a user's recent exam attempts for a given state. Purely
 * presentational — the caller (ExamPage or DashboardPage) fetches the
 * attempts array from GET /api/exam/history and passes it down.
 *
 * Accessibility: the pass/fail marker is icon + text (no colour-only
 * signal), the row uses a definition-list semantic via dd/dt elements
 * so screen readers can skip it like a table.
 */
export interface ExamHistoryPanelProps {
  stateCode: string;
  attempts: ExamHistoryEntry[];
}

function formatSeconds(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1_000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}m ${s}s`;
}

function formatDate(ms: number): string {
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(ms);
  }
}

export default function ExamHistoryPanel({
  stateCode,
  attempts,
}: ExamHistoryPanelProps) {
  const { t } = useTranslation();

  if (attempts.length === 0) {
    return (
      <section
        data-testid="exam-history-panel"
        aria-labelledby="exam-history-heading"
        className="mt-lg rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
      >
        <h2
          id="exam-history-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("exam.history.heading")}
        </h2>
        <p
          data-testid="exam-history-empty"
          className="mt-md text-sm text-neutral-800"
        >
          {t("exam.history.empty", { state: stateCode })}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="exam-history-panel"
      aria-labelledby="exam-history-heading"
      className="mt-lg rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
    >
      <h2
        id="exam-history-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("exam.history.heading")}
      </h2>
      <ul className="mt-md flex flex-col gap-sm">
        {attempts.map((a) => {
          const badgeIcon = a.passed ? "✓" : "✕";
          const badgeLabel = a.passed
            ? t("exam.history.passBadge")
            : t("exam.history.failBadge");
          const badgeClass = a.passed
            ? "bg-success/10 text-success border border-success/40"
            : "bg-danger/10 text-danger border border-danger/40";
          return (
            <li
              key={a.attemptId}
              data-testid={`exam-history-row-${a.attemptId}`}
              className="flex items-center justify-between gap-sm rounded-md border border-brand-primary/10 bg-white p-sm"
            >
              <div>
                <p className="text-sm font-semibold text-brand-primary">
                  {t("exam.history.attemptNumber", { n: a.attemptNumber })}
                </p>
                <p className="text-xs text-neutral-600">
                  {formatDate(a.submittedAtMs)} · {formatSeconds(a.timeSpentMs)}
                </p>
              </div>
              <div className="flex items-center gap-sm">
                <span className="text-sm font-semibold text-neutral-800">
                  {t("exam.history.score", {
                    correct: a.correctCount,
                    total: a.questionCount,
                  })}
                </span>
                <span
                  className={`inline-flex items-center gap-xs rounded-full px-sm py-xs text-xs font-semibold uppercase tracking-wide ${badgeClass}`}
                >
                  <span aria-hidden="true">{badgeIcon}</span>
                  <span>{badgeLabel}</span>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
